import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Phone, Mail, Calendar, Baby, ArrowRight, Shield, Clock, Star, Award } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

// Banner image URL provided by user
const BANNER_IMAGE = "https://customer-assets.emergentagent.com/job_f760df05-0c76-4756-9fc9-778e88d148c4/artifacts/2t4qe0g7_Prenatal%20Yoga%20%287%29.png";

// Logo URL provided by user
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f760df05-0c76-4756-9fc9-778e88d148c4/artifacts/drje6014_Logo.png";

const TrialCheckoutPage = () => {
  const { referralCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [validDoctor, setValidDoctor] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    expected_delivery_date: '',
    current_trimester: ''
  });
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    validateReferralAndLoadProducts();
  }, [referralCode]);

  const validateReferralAndLoadProducts = async () => {
    try {
      // Validate referral code
      const doctorRes = await axios.get(`${API_URL}/api/trial-checkout/doctor/${referralCode}`);
      setValidDoctor(doctorRes.data);

      // Load products
      const productsRes = await axios.get(`${API_URL}/api/trial-checkout/products`);
      setProducts(productsRes.data);
      
      // Pre-select first product
      if (productsRes.data.length > 0) {
        setSelectedProduct(productsRes.data[0]);
      }
    } catch (error) {
      console.error('Validation error:', error);
      setValidDoctor(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct) {
      toast.error('Please select a trial package');
      return;
    }

    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Please fill in required fields');
      return;
    }

    // Validate expected delivery date if provided
    if (formData.expected_delivery_date) {
      const selectedDate = new Date(formData.expected_delivery_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 9);
      
      if (selectedDate < today) {
        toast.error('Expected delivery date cannot be in the past');
        return;
      }
      if (selectedDate > maxDate) {
        toast.error('Expected delivery date cannot be more than 9 months from today');
        return;
      }
    }

    setProcessing(true);

    try {
      // Load Razorpay
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Payment gateway failed to load');
        setProcessing(false);
        return;
      }

      // Create order
      const orderRes = await axios.post(`${API_URL}/api/trial-checkout/create-order`, {
        referral_code: referralCode,
        product_id: selectedProduct.product_id,
        ...formData
      });

      const { order_id, amount, key_id, customer } = orderRes.data;

      // Open Razorpay
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'Soul Sanskar',
        description: selectedProduct.name,
        order_id: order_id,
        prefill: {
          name: customer.name,
          email: customer.email,
          contact: customer.phone
        },
        theme: {
          color: '#6366f1'
        },
        handler: async function(response) {
          try {
            // Verify payment + capture handoff (V24 — auto-login + welcome modal)
            const verifyRes = await axios.post(`${API_URL}/api/trial-checkout/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              referral_code: referralCode,
              product_id: selectedProduct.product_id,
              ...formData
            });

            toast.success('Payment successful!');

            // Redirect to Thank You page with customer details + SSO handoff
            const params = new URLSearchParams({
              name: formData.customer_name,
              product: selectedProduct.name,
              amount: selectedProduct.total_price.toString()
            });
            if (verifyRes.data?.lms_sso_url) {
              params.set('sso', verifyRes.data.lms_sso_url);
            }
            if (verifyRes.data?.welcome_kind) {
              params.set('welcome_kind', verifyRes.data.welcome_kind);
            }
            navigate(`/trial-thank-you?${params.toString()}`);
          } catch (error) {
            toast.error('Payment verification failed');
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  if (!validDoctor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">😔</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Invalid Link</h2>
          <p className="text-slate-600">This referral link is invalid or has expired. Please contact your doctor for a valid link.</p>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Registration Successful!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for registering! Our team will call you shortly to understand your pregnancy details and schedule your trial class accordingly.
          </p>
          <div className="bg-amber-50 rounded-lg p-4 text-left">
            <p className="text-sm text-amber-800">
              <strong>What's Next?</strong>
              <br />
              • Our team will call you within 24 hours
              <br />
              • Based on your pregnancy stage, we'll schedule your personalized trial
              <br />
              • Keep your phone handy!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center -my-4">
            <img 
              src={LOGO_URL} 
              alt="Soul Sanskar" 
              className="h-28 md:h-32 w-auto"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Secure Payment</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Hero Section - Desktop: Side by side, Mobile: Stacked */}
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Left - Banner */}
          <div className="w-full md:w-1/2 bg-white rounded-2xl shadow-lg overflow-hidden">
            <img 
              src={BANNER_IMAGE} 
              alt="Prenatal Yoga Programs" 
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Right - Form (Shows on desktop next to banner, on mobile below banner) */}
          <div className="w-full md:w-1/2 bg-white rounded-2xl shadow-xl p-5 md:p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Complete Your Registration</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Product Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Choose Your Trial Package *</label>
                <div className="space-y-2">
                  {products.map((product) => (
                    <label 
                      key={product.product_id}
                      className={`block p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedProduct?.product_id === product.product_id
                          ? 'border-rose-500 bg-rose-50'
                          : 'border-slate-200 hover:border-rose-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="product"
                          checked={selectedProduct?.product_id === product.product_id}
                          onChange={() => setSelectedProduct(product)}
                          className="w-4 h-4 text-rose-500 focus:ring-rose-500"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-semibold text-slate-800 text-sm">{product.name}</div>
                              <div className="text-xs text-slate-500">{product.description}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-base font-bold text-rose-600">₹{product.total_price}</div>
                              <div className="text-xs text-slate-400">incl. GST</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Customer Details */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    required
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email (Optional)</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {/* Expected Delivery & Trimester - Side by side on desktop, stacked on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Expected Delivery Date</label>
                  <input
                    type="date"
                    value={formData.expected_delivery_date}
                    onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    max={(() => { const d = new Date(); d.setMonth(d.getMonth() + 9); return d.toISOString().split('T')[0]; })()}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                    data-testid="expected-delivery-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Current Trimester</label>
                  <select
                    value={formData.current_trimester}
                    onChange={(e) => setFormData({ ...formData, current_trimester: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm"
                    data-testid="current-trimester"
                  >
                    <option value="">Select</option>
                    <option value="First (1-12 weeks)">First (1-12 weeks)</option>
                    <option value="Second (13-26 weeks)">Second (13-26 weeks)</option>
                    <option value="Third (27-40 weeks)">Third (27-40 weeks)</option>
                  </select>
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> After payment, our team will call you to understand your pregnancy details and schedule your trial class accordingly.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={processing}
                className="w-full bg-gradient-to-r from-rose-500 to-orange-500 text-white py-3 rounded-xl font-semibold text-base hover:from-rose-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Pay ₹{selectedProduct?.total_price || 99} & Register
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Trust Badges - Google & Trustpilot Ratings */}
              <div className="flex items-center justify-center gap-4 pt-2">
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-full">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-xs font-medium text-slate-700">4.9</span>
                  <span className="text-xs text-slate-500">Google</span>
                </div>
                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-full">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#00B67A">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                  <span className="text-xs font-medium text-slate-700">4.7</span>
                  <span className="text-xs text-slate-500">Trustpilot</span>
                </div>
              </div>

              <p className="text-center text-xs text-slate-500">
                By registering, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          </div>
        </div>

        {/* Stats Banner */}
        <div className="bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-6 mt-8 text-white">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl md:text-4xl font-bold">7500+</div>
              <div className="text-xs md:text-sm opacity-90">Happy Moms</div>
            </div>
            <div>
              <div className="text-2xl md:text-4xl font-bold">&lt;7 hrs</div>
              <div className="text-xs md:text-sm opacity-90">Average Labor Time</div>
            </div>
            <div>
              <div className="text-2xl md:text-4xl font-bold">4.9</div>
              <div className="text-xs md:text-sm opacity-90">Google Rating</div>
            </div>
          </div>
        </div>

        {/* Register for Trial Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">
            Register for Your Trial Class
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Expert-Led Sessions */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-rose-50">
              <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-rose-500" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Expert-Led Sessions</h3>
              <p className="text-sm text-slate-600">
                All instructors are <strong>M.Sc in Yoga Science</strong> with specialization in Pre-Postnatal care, bringing <strong>5+ years of experience</strong> in prenatal wellness.
              </p>
            </div>
            
            {/* Trimester-Specific */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-amber-50">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Baby className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Trimester-Specific</h3>
              <p className="text-sm text-slate-600">
                Customized exercises designed specifically for each stage of your pregnancy journey - First, Second & Third trimester.
              </p>
            </div>
            
            {/* Flexible Scheduling */}
            <div className="flex flex-col items-center text-center p-4 rounded-xl bg-green-50">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Flexible Time Slots</h3>
              <p className="text-sm text-slate-600">
                Choose from multiple convenient time slots:<br/>
                <strong className="text-green-700">Morning:</strong> 7:00 AM, 7:30 AM, 8:00 AM, 8:30 AM<br/>
                <strong className="text-green-700">Evening:</strong> 6:30 PM, 7:30 PM
              </p>
            </div>
          </div>
        </div>

        {/* What You'll Learn Section */}
        <div className="bg-slate-800 rounded-2xl p-6 mt-8 text-white">
          <h3 className="text-xl font-bold mb-4 text-center">Our Holistic Prenatal Program Includes</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">🧘‍♀️</div>
              <div className="text-sm font-medium">Prenatal Yoga</div>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">🎯</div>
              <div className="text-sm font-medium">Birthing Ball</div>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">🧠</div>
              <div className="text-sm font-medium">Garbhsanskar</div>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">💨</div>
              <div className="text-sm font-medium">Lamaze Breathing</div>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">🌟</div>
              <div className="text-sm font-medium">Hypnobirthing</div>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">💪</div>
              <div className="text-sm font-medium">Kegel Exercises</div>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">🤰</div>
              <div className="text-sm font-medium">Womb Engineering</div>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-lg">
              <div className="text-2xl mb-2">❤️</div>
              <div className="text-sm font-medium">Stress Relief</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <img 
            src={LOGO_URL} 
            alt="Soul Sanskar" 
            className="h-24 w-auto mx-auto mb-4 brightness-0 invert"
          />
          <p className="text-slate-400 text-sm">
            © 2026 Soul Sanskar Healthcare Private Limited. All rights reserved.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            GSTIN: 29AALCB5733F1ZI | Unit 101, Oxford Towers, HAL Old Airport Rd, Bangalore
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrialCheckoutPage;
